import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Add } from '@mui/icons-material'
import { Comments } from 'components'
import { Button, Card } from 'components/ui'
import {
  CrudAction,
  usePageHeader,
  usePagination,
  usePermissions,
} from 'utils/hooks'
import { successToast } from 'utils/toastService'
import { useWheatCodesService } from 'services'
import { Entity } from 'types/permissions'
import { TWheatCode } from 'types/models'
import { WheatCodeList } from './components'
import useEditModal from './dialogs/useEditModal'
import { RootState } from '../../../../store'
import {
  Container,
  ListSection,
  PageActionsButtons,
  PageActionsContainer,
  UpdateButton,
} from './WheatCodes.styled'
import {
  defaultFilterParams,
  IFilterParams,
} from '../../../../types/models/FilterSort'
import { formatFiltersSorts } from '../../../../components/Table/TableFilter/helpers'

export const WheatCodes = () => {
  const { checkPermission } = usePermissions()
  const queryClient = useQueryClient()
  const {
    getWheatCodes,
    createWheatCode,
    updateWheatCode,
    getWheatCodeAttributes,
  } = useWheatCodesService()
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination({})

  const [filterParams, setFilterParams] =
    useState<IFilterParams>(defaultFilterParams)

  const { data: wheatCodes, isLoading: isWheatCodesLoading } = useQuery({
    queryKey: ['wheat_codes_list', filterParams, currentPage, rowsPerPage],
    queryFn: () =>
      getWheatCodes(filterParams, {
        currentPage: currentPage,
        itemsPerPage: rowsPerPage,
      }),
    retry: false,
  })

  const { data: wheatCodeAttributes } = useQuery({
    queryKey: ['wheat_codes_attributes'],
    queryFn: () => getWheatCodeAttributes(),
  })

  const createWheatCodeMutation = useMutation(createWheatCode, {
    onSuccess: () => {
      void queryClient.invalidateQueries(['wheat_codes_list'])
      successToast('Wheat code created')
    },
    onError: (error: { detail?: string }) => {
      toast.error(error?.detail ? error.detail : 'Failed to create Wheat Code')
    },
  })
  const companyId = useSelector(
    (state: RootState) => state.user.profile?.company?.id
  )

  // Convert fetched attributes into validation rules
  const validationRules = useMemo(() => {
    if (!wheatCodeAttributes) return {}

    return wheatCodeAttributes?.results.reduce(
      (acc, attribute) => {
        acc[attribute.name] = {
          min: attribute.min_value,
          max: attribute.max_value,
        }
        return acc
      },
      {} as Record<string, { min: number; max: number }>
    )
  }, [wheatCodeAttributes])

  const [addModal, toggleAddModal] = useEditModal({
    initialData: {
      attributes: wheatCodeAttributes?.results?.map((attribute) => ({
        name: attribute.name,
        range_min: 0,
        range_max: 0,
        range_avg: 0,
        min_value: attribute.min_value,
        max_value: attribute.max_value,
      })),
      cost: 0,
    },
    onConfirm: async (data: TWheatCode) => {
      createWheatCodeMutation.mutate({
        ...data,
        company_id: companyId,
      })
    },
  })

  usePageHeader({
    headerTitle: 'Wheat Codes',
    actionButton: (
      <Button
        startIcon={<Add />}
        onClick={toggleAddModal}
        disabled={!checkPermission(Entity.WHEAT_CODE, CrudAction.CREATE)}
      >
        Add Wheat Code
      </Button>
    ),
  })

  const [modifiedFields, setModifiedFields] = useState<Record<number, number>>(
    {}
  )

  const handleCostChange = (id: number, newCost: number) => {
    setModifiedFields((prev) => ({
      ...prev,
      [id]: newCost,
    }))
  }

  const handleReset = () => {
    setModifiedFields({})
  }

  const handleUpdate = async () => {
    try {
      if (!wheatCodes?.results) return

      await Promise.all(
        Object.entries(modifiedFields).map(([id, cost]) => {
          const fullWheatCode = wheatCodes.results.find(
            (code: TWheatCode) => code.id === Number(id)
          )

          if (fullWheatCode) {
            const updatedWheatCode = {
              ...fullWheatCode,
              cost,
            }

            return updateWheatCode(updatedWheatCode)
          }

          return Promise.resolve()
        })
      )
      successToast('Wheat Codes cost updated')
      await queryClient.invalidateQueries(['wheat_codes_list'])
      setModifiedFields({})
    } catch (error) {
      console.error('Failed to update rows:', error)
    }
  }

  return (
    <Container>
      <ListSection>
        <WheatCodeList
          modifiedFields={modifiedFields}
          handleCostChange={handleCostChange}
          wheatCodes={wheatCodes?.results}
          pagination={wheatCodes?.pagination}
          isWheatCodesLoading={isWheatCodesLoading}
          formatFiltersSorts={(filters, sort) => {
            setFilterParams(formatFiltersSorts(filters, sort))
          }}
          setCurrentPage={setCurrentPage}
          setRowsPerPage={setRowsPerPage}
        />
        <Card flex={2} flexShrink={0}>
          <Comments />
        </Card>
      </ListSection>
      {checkPermission(Entity.WHEAT_CODE, 'cost_update') && (
        <Card>
          <PageActionsContainer>
            <PageActionsButtons>
              <Button
                variantType="secondary"
                onClick={handleReset}
                disabled={Object.keys(modifiedFields).length === 0}
              >
                Discard Changes
              </Button>
              <UpdateButton onClick={handleUpdate}>Update</UpdateButton>
            </PageActionsButtons>
          </PageActionsContainer>
        </Card>
      )}
      {addModal}
    </Container>
  )
}
